import { useState } from 'react';
import { X, Zap, Target, FileText, Trophy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

function QuickStartGuide({ onClose }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);

  const steps = [
    {
      icon: <Zap className="text-green-500 w-12 h-12" />,
      title: "Welcome to Your Command Center!",
      description: "Your dashboard shows all your negotiation stats and progress. Let's take a quick tour.",
      action: null
    },
    {
      icon: <Target className="text-green-500 w-12 h-12" />,
      title: "Start Your First Roleplay",
      description: "Practice real negotiations with AI opponents. Get instant feedback and coaching tips to improve.",
      action: { label: "Try Roleplay Now", route: "/roleplay" }
    },
    {
      icon: <FileText className="text-green-500 w-12 h-12" />,
      title: "Generate Custom Scripts",
      description: "Get AI-powered negotiation scripts tailored to your situation with win probability estimates.",
      action: { label: "Generate Script", route: "/scripts" }
    },
    {
      icon: <Trophy className="text-green-500 w-12 h-12" />,
      title: "Complete Daily Challenges",
      description: "Gamified tasks to build your skills. Earn points and climb the leaderboard!",
      action: { label: "View Challenges", route: "/challenges" }
    }
  ];

  const currentStep = steps[step];

  const handleNext = () => {
    if (step < steps.length - 1) {
      setStep(step + 1);
    } else {
      localStorage.setItem('quick_start_completed', 'true');
      onClose();
    }
  };

  const handleAction = () => {
    if (currentStep.action) {
      localStorage.setItem('quick_start_completed', 'true');
      navigate(currentStep.action.route);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm" data-testid="quick-start-guide">
      <div className="ninja-card max-w-lg w-full mx-4 relative">
        <button
          onClick={() => {
            localStorage.setItem('quick_start_completed', 'true');
            onClose();
          }}
          className="absolute top-4 right-4 text-gray-400 hover:text-white"
          data-testid="close-guide"
        >
          <X className="w-6 h-6" />
        </button>

        <div className="text-center mb-6">
          <div className="flex justify-center mb-4">
            {currentStep.icon}
          </div>
          <h2 className="font-['Outfit'] text-2xl font-bold mb-3">{currentStep.title}</h2>
          <p className="text-gray-400">{currentStep.description}</p>
        </div>

        <div className="flex gap-2 mb-4">
          {steps.map((_, idx) => (
            <div
              key={idx}
              className={`h-2 flex-1 rounded-full transition-all ${
                idx === step ? 'bg-green-500' : idx < step ? 'bg-green-500/50' : 'bg-white/10'
              }`}
            />
          ))}
        </div>

        <div className="flex gap-3">
          {currentStep.action && (
            <Button
              onClick={handleAction}
              className="flex-1 bg-green-600 hover:bg-green-500"
              data-testid="action-button"
            >
              {currentStep.action.label}
            </Button>
          )}
          <Button
            onClick={handleNext}
            variant="outline"
            className="flex-1 border-white/20 hover:bg-white/10"
            data-testid="next-button"
          >
            {step === steps.length - 1 ? 'Got it!' : 'Next'}
          </Button>
        </div>

        <div className="text-center mt-4">
          <button
            onClick={() => {
              localStorage.setItem('quick_start_completed', 'true');
              onClose();
            }}
            className="text-sm text-gray-500 hover:text-gray-400"
            data-testid="skip-button"
          >
            Skip tutorial
          </button>
        </div>
      </div>
    </div>
  );
}

export default QuickStartGuide;